import type { CSSProperties } from 'react';
import { memo } from 'react';
import type { Buckets } from '../data/buckets';
import { segHeight } from '../data/buckets';
import type { HistogramTone } from '../types';
import styles from './Columns.module.css';
import tones from './tones.module.css';

const toneClass = (tone: HistogramTone) =>
  tone.color ? styles.seg : `${styles.seg} ${tones[tone.id] ?? tones.strong}`;

export const Columns = memo(
  ({
    buckets,
    toneList,
    yScale,
    sparse = false,
  }: {
    buckets: Buckets;
    toneList: HistogramTone[];
    yScale: number;
    sparse?: boolean;
  }) => {
    /* The flexed grid is right-anchored (the newest column sits flush with
       the "now" edge), so sparse columns anchor from the right too. */
    const count = Object.values(buckets)[0]?.length ?? 0;
    const last = count - 1;
    const empty = (i: number) =>
      toneList.every((tone) => (buckets[tone.id]?.[i] ?? 0) === 0);
    return (
      <>
        {Array.from({ length: count }, (_, i) =>
          sparse && empty(i) ? null : (
            <div
              className={
                sparse ? `${styles.column} ${styles.sparse}` : styles.column
              }
              key={i}
              style={
                {
                  ...(sparse && { right: `${(last - i) * 2}px` }),
                  '--col': i,
                } as CSSProperties
              }
            >
              {toneList.map((tone) => (
                <div
                  key={tone.id}
                  className={toneClass(tone)}
                  style={{
                    height: `${segHeight(buckets[tone.id]?.[i] ?? 0, yScale)}px`,
                    ...(tone.color && { background: tone.color }),
                  }}
                />
              ))}
            </div>
          )
        )}
      </>
    );
  }
);
Columns.displayName = 'Columns';
